<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { Search, ChevronDown, Bell, Check, Cake, ClipboardCheck, BarChart3 } from 'lucide-vue-next'
import { useAuthStore } from '@/store/AuthStore'
import { useAppShellStore } from '@/store/AppShellStore'
import { useAcademicYearStore } from '@/store/AcademicYearStore'
import { paletteForKey, Palette, type PaletteName } from '@/design-system/components'
import AccountMenu from './AccountMenu.vue'
import logoUrl from '@/assets/logo-glyph.svg'

/**
 * AppTopBar (§02) — 58px global bar over the content area. Search field (opens
 * the command palette), academic-year selector (sets the global year scope) and
 * a notifications bell. On mobile (no rail) it collapses to brand + search icon
 * + bell + account avatar. Year menu and bell are mutually exclusive.
 */
const auth = useAuthStore()
const appShell = useAppShellStore()
const years = useAcademicYearStore()

// ── Notifications ──────────────────────────────────────────────────────────
// TODO: back with a real notifications endpoint. These mirror the prototype's
// demo items; `unread` drives the coral dot.
interface Notif {
  hue: PaletteName
  icon: typeof Cake
  title: string
  meta: string
}
const NOTIFS: Notif[] = [
  { hue: 'indigo', icon: Cake, title: '2 birthdays today', meta: 'Maya Jameson · Aaron Robinson' },
  { hue: 'amber', icon: ClipboardCheck, title: 'Register not taken', meta: '9 Marigold · Tuesday' },
  { hue: 'teal', icon: BarChart3, title: '42 essays to grade', meta: 'across 8 Oasis & 10 Cypress' },
]
const notifs = ref<Notif[]>(NOTIFS)
const hasUnread = computed(() => notifs.value.length > 0)

// ── Open/close (year, bell, account are mutually exclusive) ────────────────
type Pop = 'year' | 'bell' | 'account' | null
const openPop = ref<Pop>(null)
function toggle(p: Exclude<Pop, null>) {
  openPop.value = openPop.value === p ? null : p
}
function markAllRead() {
  notifs.value = []
}

const rootRef = ref<HTMLElement | null>(null)
function onClickOutside(e: MouseEvent) {
  if (rootRef.value && !rootRef.value.contains(e.target as Node)) openPop.value = null
}
function onEsc(e: KeyboardEvent) {
  if (e.key === 'Escape') openPop.value = null
}

// ── Year ───────────────────────────────────────────────────────────────────
const currentYearLabel = computed(() => years.activeYear?.label ?? '—')
function selectYear(id: string) {
  openPop.value = null
  if (id !== years.activeYearId) years.activateYear(id).catch(() => {})
}

// ── Account (mobile only) ────────────────────────────────────────────────────
const accountHue = computed(() => paletteForKey(auth.currentUser?.id ?? auth.userName ?? 'teacher'))
const initials = computed(() => {
  const name = (auth.userName || '').trim()
  if (!name) return 'U'
  const p = name.split(/\s+/)
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? (p[p.length - 1]?.[0] ?? '') : '')).toUpperCase() || 'U'
})

function hue(name: PaletteName) {
  return Palette[name]
}

onMounted(() => {
  if (!years.isLoaded) years.fetchYears()
  document.addEventListener('click', onClickOutside)
  document.addEventListener('keydown', onEsc)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside)
  document.removeEventListener('keydown', onEsc)
})
</script>

<template>
  <header ref="rootRef" class="topbar">
    <!-- Brand (mobile only — desktop brand is in the rail) -->
    <RouterLink to="/today" class="topbar__brand">
      <span
        class="topbar__brand-logo"
        :style="{ maskImage: `url(${logoUrl})`, WebkitMaskImage: `url(${logoUrl})` }"
      />
      <span class="topbar__brand-word">Smart Teacher</span>
    </RouterLink>

    <!-- Search field (desktop) -->
    <button class="topbar__search" @click="appShell.openPalette()">
      <Search :size="17" class="topbar__search-icon" />
      <span class="topbar__search-ph">Search classes, students, schemes…</span>
      <span class="topbar__kbd">⌘K</span>
    </button>

    <!-- Search icon (mobile) -->
    <button class="topbar__search-btn" aria-label="Search" @click="appShell.openPalette()">
      <Search :size="19" />
    </button>

    <div class="topbar__right">
      <!-- Academic year -->
      <div class="topbar__pop">
        <button v-if="auth.isTeacher" class="yearpill" @click.stop="toggle('year')">
          <span class="yearpill__dot" />
          <span class="yearpill__label">YEAR</span>
          <span class="yearpill__value">{{ currentYearLabel }}</span>
          <ChevronDown :size="13" class="yearpill__chev" />
        </button>
        <div v-if="openPop === 'year'" class="menu menu--year">
          <button
            v-for="y in years.years"
            :key="y.id"
            type="button"
            class="menu__item"
            :class="{ 'menu__item--sel': y.id === years.activeYearId }"
            @click="selectYear(y.id)"
          >
            <span>{{ y.label }}</span>
            <Check v-if="y.id === years.activeYearId" :size="14" />
          </button>
          <div v-if="!years.years.length" class="menu__empty">No academic years</div>
        </div>
      </div>

      <!-- Notifications -->
      <div class="topbar__pop">
        <button class="iconbtn" aria-label="Notifications" @click.stop="toggle('bell')">
          <Bell :size="19" />
          <span v-if="hasUnread" class="iconbtn__dot" />
        </button>
        <div v-if="openPop === 'bell'" class="menu menu--notif">
          <div class="notif__head">
            <span class="notif__title">Notifications</span>
            <span class="notif__count">{{ notifs.length }}</span>
          </div>
          <div v-if="!notifs.length" class="menu__empty">You’re all caught up.</div>
          <div
            v-for="(n, i) in notifs"
            :key="i"
            class="notif__row"
            :class="{ 'notif__row--first': i === 0 }"
          >
            <span class="notif__icon" :style="{ background: hue(n.hue).soft, color: hue(n.hue).ink }">
              <component :is="n.icon" :size="16" />
            </span>
            <div class="notif__text">
              <div class="notif__row-title">{{ n.title }}</div>
              <div class="notif__row-meta">{{ n.meta }}</div>
            </div>
          </div>
          <button v-if="notifs.length" type="button" class="notif__foot" @click="markAllRead">
            Mark all as read
          </button>
        </div>
      </div>

      <!-- Account (mobile only) -->
      <div class="topbar__pop topbar__account">
        <button class="account-btn" aria-label="Account" @click.stop="toggle('account')">
          <span class="account-btn__avatar" :style="{ background: accountHue.soft, color: accountHue.ink }">
            {{ initials }}
          </span>
        </button>
        <div v-if="openPop === 'account'" class="menu menu--account">
          <AccountMenu placement="down" @close="openPop = null" />
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
.topbar {
  height: 58px;
  flex-shrink: 0;
  background: var(--canvas);
  border-bottom: 1px solid var(--hairline);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 28px;
  position: relative;
  z-index: var(--z-sticky);
  font-family: var(--font-sans);
}

/* Search field (desktop) */
.topbar__search {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 400px;
  max-width: 46%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-md);
  background: var(--surface);
  cursor: pointer;
}
.topbar__search-icon {
  color: var(--ink-400);
  flex-shrink: 0;
}
.topbar__search-ph {
  flex: 1;
  text-align: left;
  font-size: 13.5px;
  color: var(--ink-400);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__kbd {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  border-radius: var(--radius-xs);
  box-shadow: inset 0 0 0 1px var(--ink-200);
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--ink-400);
}

/* Search icon + brand + account (mobile) */
.topbar__brand,
.topbar__search-btn,
.topbar__account {
  display: none;
}
.topbar__brand {
  align-items: center;
  gap: 8px;
  text-decoration: none;
}
.topbar__brand-logo {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  background: var(--primary);
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}
.topbar__brand-word {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: var(--text-primary);
}

.topbar__right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.topbar__pop {
  position: relative;
}

/* Year pill */
.yearpill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--outline-variant);
  border-radius: 9px;
  background: var(--surface);
  cursor: pointer;
}
.yearpill__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--primary);
}
.yearpill__label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--ink-400);
}
.yearpill__value {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}
.yearpill__chev {
  color: var(--ink-400);
}

/* Icon button (bell) */
.iconbtn {
  position: relative;
  width: 36px;
  height: 36px;
  border: 1px solid var(--outline-variant);
  border-radius: 9px;
  background: var(--surface);
  color: var(--ink-700);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.iconbtn__dot {
  position: absolute;
  top: 7px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: var(--radius-pill);
  background: var(--coral);
  box-shadow: 0 0 0 2px var(--surface);
}

/* Menus */
.menu {
  position: absolute;
  top: 44px;
  right: 0;
  background: var(--surface-raised);
  border: 1px solid var(--outline-variant);
  box-shadow: var(--shadow-raise);
  z-index: var(--z-dropdown);
}
.menu--year {
  width: 168px;
  border-radius: var(--radius-lg);
  padding: 6px;
}
.menu__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  cursor: pointer;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--text-primary);
}
.menu__item--sel {
  background: var(--ink-100);
  font-weight: 600;
}
.menu__empty {
  padding: 14px 16px;
  text-align: center;
  font-size: 13px;
  color: var(--ink-400);
}

/* Notifications popover */
.menu--notif {
  width: 320px;
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.notif__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 16px;
  border-bottom: 1px solid var(--hairline);
}
.notif__title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-500);
}
.notif__count {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  color: var(--ink-400);
}
.notif__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid var(--hairline);
}
.notif__row--first {
  border-top: 0;
}
.notif__icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.notif__text {
  flex: 1;
  min-width: 0;
}
.notif__row-title {
  font-size: 13.5px;
  font-weight: 500;
  color: var(--text-primary);
}
.notif__row-meta {
  margin-top: 1px;
  font-size: 12px;
  color: var(--text-tertiary);
}
.notif__foot {
  width: 100%;
  padding: 11px 16px;
  border: 0;
  border-top: 1px solid var(--hairline);
  background: transparent;
  color: var(--primary);
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

/* Account (mobile) */
.account-btn {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  padding: 0;
}
.account-btn__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}
.menu--account {
  top: 46px;
}

/* Responsive: mobile (no rail) */
@media (max-width: 767px) {
  .topbar {
    padding: 0 16px;
  }
  .topbar__search {
    display: none;
  }
  .topbar__brand,
  .topbar__search-btn,
  .topbar__account {
    display: flex;
  }
  .topbar__search-btn {
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--outline-variant);
    border-radius: 9px;
    background: var(--surface);
    color: var(--ink-700);
    cursor: pointer;
  }
}
</style>
